import { calculatePayout } from '@homestake/shared/utils';
import { observer, use$ } from '@legendapp/state/react';
import { Button, type ButtonProps } from '@mantine/core';
import Decimal from 'decimal.js';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useManualBetCashout } from '../../../hooks';
import {
  configurationState$,
  gameState$,
  manualBetState$,
  towersState$,
} from '../../../state';
import { calculateMultiplier } from '../../../utils/towers.utils';

export type ManualBetCashoutButtonProps = ButtonProps;

export const ManualBetCashoutButton = observer(
  (props: ManualBetCashoutButtonProps) => {
    const isBetting = use$(manualBetState$.isBetting);
    const betAmount = use$(manualBetState$.betAmount);
    const bricks = use$(towersState$.bricks);
    const pattern = use$(gameState$.pattern);
    const { currency, decimalScale } = use$(configurationState$);
    const { t } = useTranslation();

    const {
      handleCashout,
      mutation: { isPending },
    } = useManualBetCashout();

    // Calculate current cashout amount using useMemo
    const cashoutAmount = useMemo(() => {
      const pickedBricksCount = bricks.length;

      if (pickedBricksCount > 0) {
        const currentMultiplier = calculateMultiplier(
          pickedBricksCount,
          pattern
        );
        // Use utility function for consistent calculation
        const { payout } = calculatePayout({
          betAmount: new Decimal(betAmount),
          multiplier: new Decimal(currentMultiplier),
          withRoundedMultiplier: true,
        });

        return payout.toFixed(decimalScale, Decimal.ROUND_DOWN);
      }

      return '';
    }, [bricks.length, pattern, betAmount, decimalScale]);

    if (!isBetting) {
      return null;
    }

    return (
      <Button
        disabled={bricks.length === 0}
        fullWidth
        loading={isPending}
        onClick={handleCashout}
        size="lg"
        {...props}
      >
        {t('manualBet.cashout')}{' '}
        {cashoutAmount.length > 0 && `${cashoutAmount} ${currency}`}
      </Button>
    );
  }
);
